import type { ComponentType } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  CheckCircle,
  Handshake,
  Mail,
  Megaphone,
  Network,
  Plug,
  Waypoints,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import SEOHead from '@/components/SEOHead';
import { cn } from '@/lib/utils';

const CAREERS_EMAIL = 'careers@memara.io';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.3, ease: 'easeOut' },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

type RoleCardProps = {
  title: string;
  requirements: string[];
  emailSubject: string;
  icon: ComponentType<{ className?: string }>;
  iconColor: string;
  cardColor: string;
  getInTouchLabel: string;
};

function RoleCard({
  title,
  requirements,
  emailSubject,
  icon: Icon,
  iconColor,
  cardColor,
  getInTouchLabel,
}: RoleCardProps) {
  const mailto = `mailto:${CAREERS_EMAIL}?subject=${encodeURIComponent(emailSubject)}`;

  return (
    <Card className={cn('h-full transition-all duration-300', cardColor)}>
      <CardHeader>
        <div className="mb-4 flex items-center space-x-4">
          <div
            className={cn(
              'flex h-12 w-12 items-center justify-center rounded-lg bg-white',
              iconColor
            )}
          >
            <Icon className="h-6 w-6" />
          </div>
          <CardTitle className="text-xl font-semibold text-foreground">{title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <ul className="space-y-2">
          {requirements.map((requirement) => (
            <li key={requirement} className="flex items-start">
              <CheckCircle className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
              <span className="text-muted-foreground">{requirement}</span>
            </li>
          ))}
        </ul>
        <Button className="mt-6 w-full" variant="outline" asChild>
          <a href={mailto}>
            {getInTouchLabel}
            <ArrowRight className="ml-2 h-4 w-4" />
          </a>
        </Button>
      </CardContent>
    </Card>
  );
}

export default function CareersPage() {
  const { t } = useTranslation('careers');

  const marketingRequirements = t('roles.marketing.requirements', {
    returnObjects: true,
  }) as string[];

  const contextEngineerRequirements = t('roles.contextEngineer.requirements', {
    returnObjects: true,
  }) as string[];

  const integrationsDevrelRequirements = t('roles.integrationsDevrel.requirements', {
    returnObjects: true,
  }) as string[];

  const gtmAdvisorRequirements = t('roles.gtmAdvisor.requirements', {
    returnObjects: true,
  }) as string[];

  const memoryVizRequirements = t('roles.memoryViz.requirements', {
    returnObjects: true,
  }) as string[];

  return (
    <div className="bg-gradient-to-br from-purple-50 to-indigo-50">
      <SEOHead
        title={t('seo.title')}
        description={t('seo.description')}
        keywords={[
          'memara careers',
          'AI memory jobs',
          'context engineer',
          'developer marketing',
          'devrel engineer',
          'integrations engineer',
          'startup advisor',
          'community',
          'visualization engineer',
          'multimodal',
        ]}
        canonicalUrl="/careers"
        ogType="website"
        priority="normal"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: t('seo.structuredData.name'),
          description: t('seo.structuredData.description'),
        }}
      />

      <motion.div
        className="container mx-auto space-y-16 px-4 py-16"
        initial="initial"
        animate="animate"
        variants={staggerContainer}
      >
        <motion.section variants={fadeInUp} className="text-center">
          <h1 className="mb-6 text-4xl font-bold text-foreground md:text-5xl">
            {t('hero.title')}
          </h1>
          <p className="mx-auto mb-4 max-w-3xl text-xl text-muted-foreground">
            {t('hero.subtitle')}
          </p>
          <p className="mx-auto mb-10 max-w-3xl text-lg text-muted-foreground">
            {t('hero.description')}
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button size="lg" asChild>
              <a href={`mailto:${CAREERS_EMAIL}`}>
                {t('hero.cta.primary')}
                <Mail className="ml-2 h-5 w-5" />
              </a>
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => document.getElementById('roles')?.scrollIntoView({ behavior: 'smooth' })}
            >
              {t('hero.cta.secondary')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </motion.section>

        <motion.section variants={fadeInUp} className="mx-auto max-w-3xl text-center">
          <p className="text-lg leading-relaxed text-muted-foreground">{t('mission.brief')}</p>
        </motion.section>

        <motion.section variants={fadeInUp} className="mx-auto max-w-3xl space-y-4 text-center">
          <h2 className="text-3xl font-bold text-foreground">{t('team.title')}</h2>
          <p className="text-lg leading-relaxed text-muted-foreground">{t('team.body')}</p>
          <p className="text-lg leading-relaxed text-muted-foreground">{t('team.people')}</p>
        </motion.section>

        <motion.section id="roles" variants={fadeInUp} className="space-y-8">
          <h2 className="text-center text-3xl font-bold text-foreground">{t('roles.title')}</h2>
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            <RoleCard
              title={t('roles.marketing.title')}
              requirements={marketingRequirements}
              emailSubject={t('roles.marketing.emailSubject')}
              icon={Megaphone}
              iconColor="text-indigo-600"
              cardColor="border-indigo-200 bg-indigo-50/50"
              getInTouchLabel={t('roles.getInTouch')}
            />
            <RoleCard
              title={t('roles.contextEngineer.title')}
              requirements={contextEngineerRequirements}
              emailSubject={t('roles.contextEngineer.emailSubject')}
              icon={Network}
              iconColor="text-purple-600"
              cardColor="border-purple-200 bg-purple-50/50"
              getInTouchLabel={t('roles.getInTouch')}
            />
            <RoleCard
              title={t('roles.integrationsDevrel.title')}
              requirements={integrationsDevrelRequirements}
              emailSubject={t('roles.integrationsDevrel.emailSubject')}
              icon={Plug}
              iconColor="text-sky-600"
              cardColor="border-sky-200 bg-sky-50/50"
              getInTouchLabel={t('roles.getInTouch')}
            />
            <RoleCard
              title={t('roles.gtmAdvisor.title')}
              requirements={gtmAdvisorRequirements}
              emailSubject={t('roles.gtmAdvisor.emailSubject')}
              icon={Handshake}
              iconColor="text-emerald-600"
              cardColor="border-emerald-200 bg-emerald-50/50"
              getInTouchLabel={t('roles.getInTouch')}
            />
            <RoleCard
              title={t('roles.memoryViz.title')}
              requirements={memoryVizRequirements}
              emailSubject={t('roles.memoryViz.emailSubject')}
              icon={Waypoints}
              iconColor="text-violet-600"
              cardColor="border-violet-200 bg-violet-50/50"
              getInTouchLabel={t('roles.getInTouch')}
            />
          </div>
        </motion.section>

        <motion.section variants={fadeInUp}>
          <Card className="border-0 bg-white/80 shadow-lg backdrop-blur-sm">
            <CardContent className="space-y-6 p-8 text-center">
              <h2 className="text-3xl font-bold text-foreground">{t('contact.title')}</h2>
              <p className="mx-auto max-w-2xl text-muted-foreground">{t('contact.subtitle')}</p>
              <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
                <Button size="lg" asChild>
                  <a href={`mailto:${CAREERS_EMAIL}`}>
                    {t('contact.emailCta')}
                    <Mail className="ml-2 h-5 w-5" />
                  </a>
                </Button>
                <Button variant="outline" size="lg" asChild>
                  <a
                    href="https://x.com/memaraio"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('contact.twitterCta')}
                  </a>
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">{t('contact.remoteNote')}</p>
            </CardContent>
          </Card>
        </motion.section>
      </motion.div>
    </div>
  );
}
